import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  Card,
  Divider,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import Cookies from 'universal-cookie';
const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

function PdfUploader({ label, value, onChange, helper }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => fileInput.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please pick a PDF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is larger than 10 MB.');
      return;
    }
    setUploading(true);
    try {
      const base64 = await readFileAsBase64(file);
      const { data } = await axios.post(
        `${API_URL}/api/admin/uploads/pdf`,
        { base64, filename: file.name, folder: 'settings' },
        authHeaders()
      );
      onChange(data.url || '');
      toast.success('PDF uploaded.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      {helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helper}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handlePick} disabled={uploading}>
          {uploading ? <CircularProgress size={18} /> : value ? 'Replace PDF' : 'Upload PDF'}
        </Button>
        {value && (
          <>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
              {value}
            </Typography>
            <IconButton size="small" component="a" href={value} target="_blank" rel="noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onChange('')}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
      <input type="file" accept="application/pdf" ref={fileInput} hidden onChange={handleFile} />
    </Box>
  );
}

function ImageUploader({ label, value, onChange, helper }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => fileInput.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is larger than 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const base64 = await readFileAsBase64(file);
      const { data } = await axios.post(
        `${API_URL}/api/admin/uploads/image`,
        { base64, folder: 'settings' },
        authHeaders()
      );
      onChange(data.url || '');
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      {helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helper}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handlePick} disabled={uploading}>
          {uploading ? <CircularProgress size={18} /> : value ? 'Replace image' : 'Upload image'}
        </Button>
        {value && (
          <>
            <Box
              component="img"
              src={value}
              alt=""
              sx={{
                height: 48,
                maxWidth: 160,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
              {value}
            </Typography>
            <IconButton size="small" component="a" href={value} target="_blank" rel="noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onChange('')}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
      <input type="file" accept="image/*" ref={fileInput} hidden onChange={handleFile} />
    </Box>
  );
}

const SETTINGS_KEYS = {
  // Phase 2 — contact form thank-you emails
  tutoring_enquiry_pdf_url: '',
  consultation_booking_pdf_url: '',
  tutoring_enquiry_image_url: '',
  consultation_booking_image_url: '',
  tutoring_enquiry_email_subject: '',
  tutoring_enquiry_email_message: '',
  consultation_booking_email_subject: '',
  consultation_booking_email_message: '',
  // Bank-transfer block
  bank_transfer_account_name: '',
  bank_transfer_sort_code: '',
  bank_transfer_account_number: '',
  // Phase 3 — Term-dates broadcast
  term_dates_broadcast_subject: '',
  term_dates_broadcast_message: '',
  // Phase 3 — Class announcement
  class_announcement_email_subject: '',
  class_announcement_email_message: '',
  // Phase 3 — Progress reports
  progress_report_email_subject: '',
  progress_report_email_message: '',
  // Phase 3 — Tutor payouts
  tutor_payout_email_subject: '',
  tutor_payout_email_message: '',
  tutor_payment_made_email_subject: '',
  tutor_payment_made_email_message: '',
  // Phase 3 — Waiting list thank-you
  waiting_list_email_subject: '',
  waiting_list_email_message: '',
  waiting_list_image_url: '',
  // Phase 4 — Waiting list → parent account welcome email
  wl_welcome_email_subject: '',
  wl_welcome_email_message: '',
  // Phase 3 — Messaging
  message_notification_subject: '',
  // Phase 3 — Renewal surveys
  renewal_survey_email_subject: '',
  renewal_survey_followup_subject: '',
  // Phase 4 — Account status (admin approval flow)
  signup_admin_review_subject: '',
  signup_admin_review_message: '',
  account_approved_subject: '',
  account_approved_message: '',
  account_declined_subject: '',
  account_declined_message: '',
  // Phase 4 — Parent ↔ student invitations
  parent_invitation_subject: '',
  parent_invitation_message: '',
  student_invitation_subject: '',
  student_invitation_message: '',
};

const TABS = [
  { value: 'emails', label: 'Contact-form emails' },
  { value: 'bank', label: 'Bank-transfer details' },
  { value: 'phase3', label: 'Operational emails' },
];

// Phase 3 fields the "Operational emails" tab renders. Defined as a flat
// array so re-ordering / adding fields is a one-line change.
const PHASE_3_FIELDS = [
  { group: 'Term dates broadcast',
    fields: [
      { key: 'term_dates_broadcast_subject', label: 'Email subject' },
      { key: 'term_dates_broadcast_message', label: 'Intro message (HTML)', multi: true },
    ] },
  { group: 'Class announcement (on publish)',
    fields: [
      { key: 'class_announcement_email_subject', label: 'Email subject' },
      { key: 'class_announcement_email_message', label: 'Intro message (HTML)', multi: true },
    ] },
  { group: 'Progress reports',
    fields: [
      { key: 'progress_report_email_subject', label: 'Email subject' },
      { key: 'progress_report_email_message', label: 'Email body (HTML)', multi: true },
    ] },
  { group: 'Tutor payouts',
    fields: [
      { key: 'tutor_payout_email_subject', label: 'Slip email subject' },
      { key: 'tutor_payout_email_message', label: 'Slip email body (HTML)', multi: true },
      { key: 'tutor_payment_made_email_subject', label: '"You have been paid" subject' },
      { key: 'tutor_payment_made_email_message', label: '"You have been paid" body (HTML)', multi: true },
    ] },
  { group: 'Waiting list thank-you',
    fields: [
      { key: 'waiting_list_email_subject', label: 'Email subject' },
      { key: 'waiting_list_email_message', label: 'Email body (HTML)', multi: true },
      { key: 'waiting_list_image_url', label: 'Banner image URL (use Image uploader on Contact-form emails tab if needed)' },
    ] },
  { group: 'Waiting list — parent welcome (after Create account)',
    fields: [
      { key: 'wl_welcome_email_subject', label: 'Email subject' },
      { key: 'wl_welcome_email_message', label: 'Email body — placeholders: {{firstName}}, {{setPasswordUrl}}', multi: true },
    ] },
  { group: 'In-app messaging',
    fields: [
      { key: 'message_notification_subject', label: '"New message" notification subject' },
    ] },
  { group: 'Renewal surveys',
    fields: [
      { key: 'renewal_survey_email_subject', label: 'Initial survey email subject' },
      { key: 'renewal_survey_followup_subject', label: 'Follow-up email subject' },
    ] },
  { group: 'Account status — admin approval flow',
    fields: [
      { key: 'signup_admin_review_subject', label: '"New signup awaiting review" subject (sent to admin)' },
      { key: 'signup_admin_review_message', label: '"New signup awaiting review" body — HTML (sent to admin)', multi: true },
      { key: 'account_approved_subject', label: '"Account approved" subject (sent to user)' },
      { key: 'account_approved_message', label: '"Account approved" body — HTML (sent to user)', multi: true },
      { key: 'account_declined_subject', label: '"Account declined" subject (sent to user)' },
      { key: 'account_declined_message', label: '"Account declined" body — HTML (sent to user)', multi: true },
    ] },
  { group: 'Invitations (parent ↔ student linking)',
    fields: [
      { key: 'parent_invitation_subject', label: '"You\'ve been invited as a parent" subject' },
      { key: 'parent_invitation_message', label: 'Body — HTML. Placeholders: {{inviterName}}, {{firstName}}, {{acceptUrl}}', multi: true },
      { key: 'student_invitation_subject', label: '"You\'ve been invited as a student" subject' },
      { key: 'student_invitation_message', label: 'Body — HTML. Placeholders: {{inviterName}}, {{firstName}}, {{acceptUrl}}', multi: true },
    ] },
];

function Settings() {
  const navigate = useNavigate();
  const [values, setValues] = useState(SETTINGS_KEYS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('emails');

  useEffect(() => {
    axios
      .get(`${API_URL}/api/settings/`)
      .then((res) => {
        const map = { ...SETTINGS_KEYS };
        (res.data || []).forEach((item) => {
          if (item?.key in map) map[item.key] = item.value || '';
        });
        setValues(map);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, []);

  const update = (key) => (e) => {
    const val = typeof e === 'string' ? e : e?.target?.value;
    setValues((v) => ({ ...v, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = Object.entries(values).map(([key, value]) => ({ key, value: value || '' }));

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/settings`, { data }, authHeaders());
      toast.success(res.data?.message || 'Settings saved.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
        <Typography variant="h4" sx={{ fontFamily: 'Inter', fontWeight: 600 }}>
          Settings
        </Typography>
      </Stack>

      <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        {tab === 'emails' && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sent automatically when a parent submits the public contact form. The PDFs are attached to each email.
              Leave subject/message blank to use the built-in defaults.
            </Typography>
                    <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> All the site-wide configuration: email template copy, bank-transfer details, invoice numbering, engagement rules, thresholds.<br />
                            <strong>2.</strong> Change a value, click <strong>Save settings</strong> at the bottom of the tab.<br />
                            <strong>3.</strong> Changes take effect on the next email sent / next invoice generated — historical records keep the values they were created with.<br />
                        </Typography>
                    </HowItWorksCard>

            <Typography sx={{ fontWeight: 600, mt: 1 }}>Tutoring enquiry</Typography>
            <Stack spacing={2} sx={{ mt: 1, mb: 2 }}>
              <TextField
                label="Email subject"
                value={values.tutoring_enquiry_email_subject}
                onChange={update('tutoring_enquiry_email_subject')}
                fullWidth
              />
              <TextField
                label="Warm message (HTML supported)"
                value={values.tutoring_enquiry_email_message}
                onChange={update('tutoring_enquiry_email_message')}
                fullWidth
                multiline
                minRows={4}
              />
              <ImageUploader
                label="Banner image"
                helper="Shown at the top of the tutoring-enquiry thank-you email. Leave blank for a text-only email."
                value={values.tutoring_enquiry_image_url}
                onChange={(url) => setValues((v) => ({ ...v, tutoring_enquiry_image_url: url }))}
              />
              <PdfUploader
                label="Free-resource PDF"
                helper="Attached to every tutoring-enquiry thank-you email."
                value={values.tutoring_enquiry_pdf_url}
                onChange={(url) => setValues((v) => ({ ...v, tutoring_enquiry_pdf_url: url }))}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontWeight: 600 }}>Consultation booking</Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Email subject"
                value={values.consultation_booking_email_subject}
                onChange={update('consultation_booking_email_subject')}
                fullWidth
              />
              <TextField
                label="Warm message (HTML supported)"
                value={values.consultation_booking_email_message}
                onChange={update('consultation_booking_email_message')}
                fullWidth
                multiline
                minRows={4}
              />
              <ImageUploader
                label="Banner image"
                helper="Shown at the top of the consultation-booking confirmation. Leave blank for a text-only email."
                value={values.consultation_booking_image_url}
                onChange={(url) => setValues((v) => ({ ...v, consultation_booking_image_url: url }))}
              />
              <PdfUploader
                label="Free-resource PDF"
                helper="Attached to every consultation-booking confirmation."
                value={values.consultation_booking_pdf_url}
                onChange={(url) => setValues((v) => ({ ...v, consultation_booking_pdf_url: url }))}
              />
            </Stack>
          </Card>
        )}

        {tab === 'bank' && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Printed on every new invoice. Existing invoices keep the details that were in place when they were generated.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Account name"
                value={values.bank_transfer_account_name}
                onChange={update('bank_transfer_account_name')}
                fullWidth
              />
              <TextField
                label="Sort code"
                value={values.bank_transfer_sort_code}
                onChange={update('bank_transfer_sort_code')}
                fullWidth
              />
              <TextField
                label="Account number"
                value={values.bank_transfer_account_number}
                onChange={update('bank_transfer_account_number')}
                fullWidth
              />
            </Stack>
          </Card>
        )}

        {tab === 'phase3' && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Subject lines and email bodies for every automated email from the teaching
              operations layer (term dates, classes, reports, payouts, messaging, waiting list,
              renewals). Leave blank to use the built-in defaults.
            </Typography>
            <Stack spacing={3}>
              {PHASE_3_FIELDS.map((g) => (
                <Box key={g.group}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{g.group}</Typography>
                  <Stack spacing={2}>
                    {g.fields.map((f) => (
                      <TextField
                        key={f.key}
                        label={f.label}
                        value={values[f.key] || ''}
                        onChange={update(f.key)}
                        multiline={!!f.multi}
                        minRows={f.multi ? 3 : undefined}
                        fullWidth
                      />
                    ))}
                  </Stack>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Stack>
          </Card>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={() => navigate(-1)} variant="outlined">
            Back
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
}

export default Settings;